import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, AlertTriangle, Loader2, Check } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import LeagueCalendar from "./LeagueCalendar";
import { useOrgHall } from "@/hooks/useOrgHall";
// UPPDRAG-54 / UPPDRAG-55: helpers for minute-overlap conflict detection
// (replaces the legacy exact-key matcher) and auto end-time computation
// when admin picks a start time.
import {
  findCourtConflicts,
  findAllSpeltidOverlaps,
  addMinutesToTime,
  type SpeltidDivision,
} from "@/lib/speltiderConflicts";

// ── Types ───────────────────────────────────────────────────────────────

interface TimeBlock {
  start: string;
  end: string;
  court_ids: string[];
}

type PlayScheduleMap = Record<string, TimeBlock[]>;

interface Division {
  id: string;
  name: string;
  level: number;
  play_schedule: PlayScheduleMap | null;
  team_count: number;
  // UPPDRAG-46: per-division override of leagues.match_duration_minutes.
  // NULL = inherit; consumers do `div.match_duration_minutes ?? matchDuration`.
  match_duration_minutes: number | null;
}

interface Conflict {
  day: string;
  time: string;
  courtId: string;
  courtName: string;
  divisions: string[];
}

interface LeagueDivisionTimesProps {
  leagueId: string;
  courts: Array<{ id: string; name: string }>;
  matchDuration: number;
  leagueStartDate?: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────

const DAYS = [
  { key: "monday", label: "Måndag" },
  { key: "tuesday", label: "Tisdag" },
  { key: "wednesday", label: "Onsdag" },
  { key: "thursday", label: "Torsdag" },
  { key: "friday", label: "Fredag" },
  { key: "saturday", label: "Lördag" },
  { key: "sunday", label: "Söndag" },
];

function generateTimeOptions(): string[] {
  const options: string[] = [];
  for (let h = 5; h <= 23; h++) {
    options.push(`${String(h).padStart(2, "0")}:00`);
    if (h < 23) options.push(`${String(h).padStart(2, "0")}:30`);
  }
  return options;
}

const TIME_OPTIONS = generateTimeOptions();

const DIVISION_COLORS: Array<{ bg: string; border: string; text: string }> = [
  { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-700" },
  { bg: "bg-green-50", border: "border-green-300", text: "text-green-700" },
  { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-700" },
  { bg: "bg-purple-50", border: "border-purple-300", text: "text-purple-700" },
  { bg: "bg-cyan-50", border: "border-cyan-300", text: "text-cyan-700" },
];

function getDivisionColor(level: number) {
  if (level >= 1 && level <= DIVISION_COLORS.length) return DIVISION_COLORS[level - 1];
  return { bg: "bg-gray-50", border: "border-gray-300", text: "text-gray-700" };
}

function toMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
}

function computeParallelMatches(block: TimeBlock, matchDuration: number): number {
  const totalMinutes = toMinutes(block.end) - toMinutes(block.start);
  const slotsPerCourt = Math.floor(totalMinutes / matchDuration);
  const courtCount = block.court_ids.length;
  return slotsPerCourt * courtCount;
}

// ── Component ───────────────────────────────────────────────────────────

export default function LeagueDivisionTimes({ leagueId, courts, matchDuration, leagueStartDate }: LeagueDivisionTimesProps) {
  const { t } = useTranslation("admin");
  const queryClient = useQueryClient();
  const { hallHours } = useOrgHall();
  // UPPDRAG-30: NodeJS namespace not declared in browser TS lib;
  // ReturnType<typeof setTimeout> is the cross-runtime equivalent.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  // Local state: division_id -> play_schedule
  const [schedules, setSchedules] = useState<Record<string, PlayScheduleMap>>({});

  // ── UPPDRAG-43 ─────────────────────────────────────────────────────
  // Cheap count of league_matches for this league, used to decide
  // whether to render the empty-state hint above the schedule view.
  // Matches are only created at publish-time (AdminLeagues.tsx
  // publishMutation), so an unpublished league always shows 0 here.
  // See audit-reports/league-schedule-preview/01-investigation.md for
  // the full chain that explains why the calendar appears empty.
  const { data: matchCount = 0 } = useQuery<number>({
    queryKey: ["league-matches-count", leagueId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("league_matches")
        .select("id", { count: "exact", head: true })
        .eq("league_id", leagueId)
        .in("status", ["scheduled", "rescheduled"]);
      if (error) throw error;
      return count ?? 0;
    },
  });

  // ── Fetch divisions ─────────────────────────────────────────────────

  const { data: divisions = [] } = useQuery<Division[]>({
    queryKey: ["division-times", leagueId],
    queryFn: async () => {
      // UPPDRAG-46: include match_duration_minutes so per-division override
      // is available to computeParallelMatches + meta-line rendering. The
      // generated supabase types lag the migration here (regenerated on
      // next routine refresh); cast unblocks the compile-time check, the
      // runtime cast below preserves null-safety.
      const { data: divs, error } = await (supabase as any)
        .from("divisions")
        .select("id, name, level, play_schedule, match_duration_minutes")
        .eq("league_id", leagueId)
        .order("level");
      if (error) throw error;

      // Count teams per division
      const { data: teams } = await supabase
        .from("teams")
        .select("division_id")
        .eq("league_id", leagueId);

      const counts: Record<string, number> = {};
      (teams ?? []).forEach((t) => {
        if (t.division_id) counts[t.division_id] = (counts[t.division_id] || 0) + 1;
      });

      return ((divs ?? []) as Array<{
        id: string;
        name: string;
        level: number;
        play_schedule: unknown;
        match_duration_minutes: number | null;
      }>).map((d) => ({
        id: d.id,
        name: d.name,
        level: d.level,
        play_schedule: (d.play_schedule as PlayScheduleMap) ?? null,
        team_count: counts[d.id] ?? 0,
        // UPPDRAG-46: optional per-division override; NULL = inherit.
        match_duration_minutes: d.match_duration_minutes ?? null,
      }));
    },
  });

  // Sync fetched schedules into local state
  useEffect(() => {
    const map: Record<string, PlayScheduleMap> = {};
    for (const d of divisions) {
      map[d.id] = d.play_schedule ?? {};
    }
    setSchedules(map);
  }, [divisions]);

  // ── Debounced save ──────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: async ({ divisionId, schedule }: { divisionId: string; schedule: PlayScheduleMap }) => {
      // Clean empty days
      const cleaned: PlayScheduleMap = {};
      for (const [day, blocks] of Object.entries(schedule)) {
        if (blocks && blocks.length > 0) cleaned[day] = blocks;
      }
      const { error } = await supabase
        .from("divisions")
        .update({ play_schedule: cleaned as any })
        .eq("id", divisionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["division-times", leagueId] });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: (e: Error) => {
      toast.error(e.message || t("toast.couldNotSave"));
      setSaveStatus('idle');
    },
  });

  const debouncedSave = useCallback((divisionId: string, schedule: PlayScheduleMap) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');
    saveTimerRef.current = setTimeout(() => {
      saveMutation.mutate({ divisionId, schedule });
    }, 500);
  }, [saveMutation]);

  // ── Schedule editing ────────────────────────────────────────────────

  function updateSchedule(divisionId: string, updater: (prev: PlayScheduleMap) => PlayScheduleMap) {
    setSchedules((prev) => {
      const current = prev[divisionId] ?? {};
      const next = updater(current);
      debouncedSave(divisionId, next);
      return { ...prev, [divisionId]: next };
    });
  }

  function addTimeBlock(divisionId: string) {
    updateSchedule(divisionId, (prev) => {
      const day = "thursday";
      const existing = prev[day] ?? [];
      return {
        ...prev,
        [day]: [...existing, { start: "17:00", end: "19:00", court_ids: [] }],
      };
    });
  }

  function removeTimeBlock(divisionId: string, day: string, blockIndex: number) {
    updateSchedule(divisionId, (prev) => {
      const blocks = [...(prev[day] ?? [])];
      blocks.splice(blockIndex, 1);
      return { ...prev, [day]: blocks };
    });
  }

  function updateBlockDay(divisionId: string, oldDay: string, blockIndex: number, newDay: string) {
    updateSchedule(divisionId, (prev) => {
      const blocks = [...(prev[oldDay] ?? [])];
      const [block] = blocks.splice(blockIndex, 1);
      const targetBlocks = [...(prev[newDay] ?? []), block];
      return { ...prev, [oldDay]: blocks, [newDay]: targetBlocks };
    });
  }

  function updateBlockField(divisionId: string, day: string, blockIndex: number, field: "start" | "end", value: string) {
    updateSchedule(divisionId, (prev) => {
      const blocks = [...(prev[day] ?? [])];
      const next = { ...blocks[blockIndex], [field]: value };
      // UPPDRAG-55: when admin picks a start time, auto-fill end using
      // the division's effective match duration (per-division override
      // from UPPDRAG-46 falls back to league-wide match_duration). Admin
      // can still edit end manually after — this just spares the second
      // click in the common case.
      if (field === "start") {
        const div = divisions.find((d) => d.id === divisionId);
        const effDuration = (div as { match_duration_minutes?: number | null } | undefined)?.match_duration_minutes ?? matchDuration;
        next.end = addMinutesToTime(value, effDuration);
      }
      blocks[blockIndex] = next;
      return { ...prev, [day]: blocks };
    });
  }

  function toggleCourt(divisionId: string, day: string, blockIndex: number, courtId: string) {
    updateSchedule(divisionId, (prev) => {
      const blocks = [...(prev[day] ?? [])];
      const block = { ...blocks[blockIndex] };
      const ids = [...(block.court_ids ?? [])];
      const idx = ids.indexOf(courtId);
      if (idx >= 0) ids.splice(idx, 1);
      else ids.push(courtId);
      block.court_ids = ids;
      blocks[blockIndex] = block;
      return { ...prev, [day]: blocks };
    });
  }

  // ── Conflict detection — UPPDRAG-54 ─────────────────────────────────
  // Replaced the legacy exact-key matcher (which only caught identical
  // start/end strings, missing 17:30-19:00 vs 18:00-19:30 overlaps) with
  // the minute-overlap helper. The helper is also reused per-court in
  // the badge render below for real-time disable + tooltip.

  // Snapshot of every division's current speltider schedule, in the shape
  // the helper expects. Recomputed every render (cheap; speltider grids
  // are tiny — 5-10 divisions × few blocks each).
  const speltidDivisions: SpeltidDivision[] = divisions.map((d) => ({
    id: d.id,
    name: d.name,
    schedule: schedules[d.id] ?? {},
  }));

  const conflicts: Conflict[] = findAllSpeltidOverlaps(speltidDivisions).map((o) => ({
    day: DAYS.find((d) => d.key === o.day)?.label ?? o.day,
    time: `${o.rangeA.start}-${o.rangeA.end} / ${o.rangeB.start}-${o.rangeB.end}`,
    courtId: o.courtId,
    courtName: courts.find((c) => c.id === o.courtId)?.name ?? o.courtId,
    divisions: o.divisionNames,
  }));

  // ── Render ──────────────────────────────────────────────────────────

  if (divisions.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        {t("leagues.divisionTimes.createDivisionsFirst")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Save status indicator */}
      {saveStatus !== 'idle' && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          {saveStatus === 'saving' && <><Loader2 className="h-3 w-3 animate-spin" /> {t("leagues.form.saving")}</>}
          {saveStatus === 'saved' && <><Check className="h-3 w-3 text-green-500" /> {t("leagues.form.saved")}</>}
        </div>
      )}

      {divisions.map((div) => {
        const color = getDivisionColor(div.level);
        const schedule = schedules[div.id] ?? {};
        // UPPDRAG-46: effective match duration for THIS division — falls
        // back to the league-wide value when no per-division override is
        // set. Used in the "X banor × Y min = Z matcher" summary below.
        const effectiveMatchDuration = div.match_duration_minutes ?? matchDuration;

        // Flatten blocks for rendering: [{ day, blockIndex, block }]
        const flatBlocks: Array<{ day: string; blockIndex: number; block: TimeBlock }> = [];
        for (const [day, blocks] of Object.entries(schedule)) {
          (blocks ?? []).forEach((block, i) => {
            flatBlocks.push({ day, blockIndex: i, block });
          });
        }

        return (
          <Card key={div.id} className={`p-4 border-2 ${color.border} ${color.bg}`}>
            <div className="flex items-center gap-2 mb-3">
              <h3 className={`font-semibold text-sm ${color.text}`}>{div.name}</h3>
              <Badge variant="outline" className="text-xs">
                {div.team_count} lag
              </Badge>
            </div>

            <div className="space-y-3">
              {flatBlocks.map(({ day, blockIndex, block }, flatIdx) => (
                <div key={`${day}-${blockIndex}-${flatIdx}`} className="flex flex-wrap items-start gap-2 p-2 rounded bg-white/60 border">
                  {/* Day select */}
                  <Select
                    value={day}
                    onValueChange={(newDay) => updateBlockDay(div.id, day, blockIndex, newDay)}
                  >
                    <SelectTrigger className="w-[120px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS.map((d) => (
                        <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Start time */}
                  <Select
                    value={block.start}
                    onValueChange={(v) => updateBlockField(div.id, day, blockIndex, "start", v)}
                  >
                    <SelectTrigger className="w-[90px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <span className="text-xs text-muted-foreground self-center">–</span>

                  {/* End time */}
                  <Select
                    value={block.end}
                    onValueChange={(v) => updateBlockField(div.id, day, blockIndex, "end", v)}
                  >
                    <SelectTrigger className="w-[90px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Court badges — UPPDRAG-54: per-court real-time conflict
                      detection. Disable + red ring + tooltip when picking
                      this court would conflict with another division's
                      block on the same day at overlapping minutes. */}
                  <TooltipProvider delayDuration={150}>
                    <div className="flex flex-wrap gap-1">
                      {courts.map((court) => {
                        const active = (block.court_ids ?? []).includes(court.id);
                        // Compute conflict only for inactive courts (admin
                        // already accepted the conflict if they have it
                        // selected — bottom panel + minute-overlap warning
                        // handles that case).
                        const conflictsForCourt = active ? [] : findCourtConflicts(speltidDivisions, {
                          day,
                          courtId: court.id,
                          start: block.start,
                          end: block.end,
                          excludeDivisionId: div.id,
                          excludeBlockIndex: blockIndex,
                        });
                        const hasConflict = conflictsForCourt.length > 0;
                        const badgeEl = (
                          <Badge
                            key={court.id}
                            variant={active ? "default" : "outline"}
                            className={`text-xs ${active ? "cursor-pointer" : hasConflict ? "cursor-not-allowed opacity-60 ring-1 ring-destructive text-destructive" : "cursor-pointer opacity-50"}`}
                            onClick={() => {
                              if (hasConflict) return; // UPPDRAG-54: refuse the click
                              toggleCourt(div.id, day, blockIndex, court.id);
                            }}
                          >
                            {court.name}
                          </Badge>
                        );
                        if (!hasConflict) return badgeEl;
                        // Wrap conflicting badges in a Tooltip explaining
                        // which division/time blocks this would clash with.
                        return (
                          <Tooltip key={court.id}>
                            <TooltipTrigger asChild>{badgeEl}</TooltipTrigger>
                            <TooltipContent side="top" className="text-xs max-w-[260px]">
                              <p className="font-medium mb-0.5">Krock — kan inte väljas:</p>
                              {conflictsForCourt.map((c, i) => (
                                <p key={i}>
                                  {c.conflictingDivisionName}: {c.start}–{c.end}
                                </p>
                              ))}
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </TooltipProvider>

                  {/* Delete block */}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive shrink-0"
                    onClick={() => removeTimeBlock(div.id, day, blockIndex)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>

                  {/* Summary — UPPDRAG-46: per-division effective duration. */}
                  {(block.court_ids?.length ?? 0) > 0 && (
                    <div className="w-full text-[11px] text-muted-foreground pl-1">
                      {block.court_ids.length} banor &times; {effectiveMatchDuration} min = {computeParallelMatches(block, effectiveMatchDuration)} matcher
                    </div>
                  )}
                </div>
              ))}

              <Button
                size="sm"
                variant="outline"
                className="text-xs gap-1"
                onClick={() => addTimeBlock(div.id)}
              >
                <Plus className="h-3 w-3" /> {t("leagues.divisionTimes.addTime")}
              </Button>
            </div>
          </Card>
        );
      })}

      {/* Conflict detection */}
      {conflicts.length > 0 && (
        <Card className="p-4 border-amber-300 bg-amber-50">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h3 className="font-semibold text-sm text-amber-700">{t("leagues.divisionTimes.conflicts")}</h3>
          </div>
          <div className="space-y-1">
            {conflicts.map((c, i) => (
              <p key={i} className="text-xs text-amber-700">
                {c.day} {c.time} &middot; {c.courtName} &mdash; {c.divisions.join(", ")}
              </p>
            ))}
          </div>
        </Card>
      )}

      {/* UPPDRAG-43: this section was previously labelled "Förhandsgranskning"
          which made admins think it would synthesize a preview from the
          configured speltider. It actually reads `league_matches` from the DB
          — those rows only exist after the league has been published
          (AdminLeagues.tsx → publishMutation, requires ≥ 2 teams per
          division). Until then the calendar is empty by design (the gray
          dots are existing court bookings overlaid as background context).

          Renamed to "Schemavy" + added an empty-state hint above the
          calendar when no matches exist yet. The calendar still renders
          so admins can see the booking context; it just no longer pretends
          to be a synthetic preview. Full chain in
          audit-reports/league-schedule-preview/01-investigation.md. */}
      {hallHours && (
        <div className="mt-4">
          <h3 className="font-semibold text-sm mb-2">{t("leagues.divisionTimes.scheduleView")}</h3>
          {matchCount === 0 && (
            <Card className="p-3 mb-2 bg-muted/30 border-dashed">
              <p className="text-sm">
                {t("leagues.divisionTimes.scheduleViewEmpty")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("leagues.divisionTimes.scheduleViewEmptyHint")}
              </p>
            </Card>
          )}
          <LeagueCalendar
            leagueId={leagueId}
            courts={courts}
            hallHours={hallHours}
            matchDuration={matchDuration}
            initialDate={leagueStartDate}
            readOnly
          />
        </div>
      )}
    </div>
  );
}
